import { useState, useEffect } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useBusy, useBusyKeys } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { Link } from 'react-router-dom';
import {
  createRing, deleteRing,
  addTournamentManager, removeTournamentManager,
  getAgeGroups, getUsers,
} from '../../api/client';
import api from '../../api/client';
import { apiError } from '../../hooks/useApiCall';
import { useI18n } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import Avatar from '../../components/shared/Avatar';
import { PencilIcon, PhoneIcon, DotIcon, XMarkIcon } from '../../components/shared/Icons';
import { personName } from '../../utils/personName';
import ModalBackdrop from '../../components/shared/ModalBackdrop';
import ModalHeader from '../../components/shared/ModalHeader';
import ModalFooter from '../../components/shared/ModalFooter';
import LiveBoutRows from '../../components/tournament/LiveBoutRows';
import RingFields, { EMPTY_RING_DRAFT, ringBody, ringDraftOf, type RingDraft } from '../../components/tournament/RingFields';
import { BADGE_XXS_TALL } from '../../components/shared/badgeSize';
import { boutScoreState } from '../../utils/tournamentState';
import type {
  Tournament, Bout, Bracket, Registration, Ring,
  TournamentManager, TournamentReferee, UserListRow,
} from '../../types';

/** The ring in the Edit modal: its id and the form values (`RingDraft`, an
 *  empty string = no restriction). */
type EditingRing = RingDraft & { id: string };

interface OverviewTabProps {
  id: string;
  tournament: Tournament;
  /** Fetches the tournament again, under the order guard of #490. */
  refreshTournament: () => Promise<void>;
  bouts: Bout[];
  brackets: Bracket[];
  registrations: Registration[];
  referees: TournamentReferee[];
  managers: TournamentManager[];
  rings: Ring[];
  currentBoutsAll: Bout[];
  currentBoutsByRing: Record<string, Bout>;
  managesThisTournament: boolean;
  setTab: (tab: string) => void;
  reload: () => Promise<void>;
}

export default function OverviewTab({
  id, tournament, refreshTournament, bouts, brackets, registrations, referees, managers, rings,
  currentBoutsAll, currentBoutsByRing, managesThisTournament, setTab, reload, }: OverviewTabProps) {
  const { showError, showSuccess, showWarning, showToast } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  const { t } = useI18n();
  const { boutCount } = useLabels();
  const allScored = boutScoreState(bouts).all;

  // Ring management
  const [showRingForm, setShowRingForm] = useState(false);
  const [newRing, setNewRing] = useState<RingDraft>(EMPTY_RING_DRAFT);
  const [editingRing, setEditingRing] = useState<EditingRing | null>(null);

  // Manager management
  const [showAddManager, setShowAddManager] = useState(false);
  const [managerEmail, setManagerEmail] = useState('');
  const [tmUsers, setTmUsers] = useState<UserListRow[]>([]);

  // #605 — one write at a time: a second tap sends no copy. The Save and the
  // Delete of the Edit Ring modal share one lock. The remove X has a lock for
  // each manager.
  const addingRing = useBusy();
  const ringEdit = useBusy();
  const addingManager = useBusy();
  const removingManager = useBusyKeys();

  // The ring min/max age-group pickers read the reference endpoint, which
  // returns the IFMA v3.057 codes in the one order (#344). The legacy v2.04
  // codes of past tournaments are display-only; no picker offers them.
  const [ringAgeCodes, setRingAgeCodes] = useState<string[]>([]);
  useEffect(() => {
    getAgeGroups().then(r => setRingAgeCodes(r.data.map(ag => ag.code))).catch(err => { setRingAgeCodes([]); showError(err); });
  }, []);

  async function handleAddRing(e: React.FormEvent) {
    e.preventDefault();
    const body = ringBody(newRing);
    if (!body.name) return;
    await addingRing.run(async () => {
      try {
        await createRing(id, body.name, body);
      } catch (err) { showError(err); return; }
      // #419 — the ring exists. A failed refetch is a warning, not an error.
      await afterWrite({ source: 'OverviewTab:add-ring', success: t.tournament.ringAdded.replace('{name}', body.name), refresh: refreshTournament });
      setNewRing(EMPTY_RING_DRAFT); setShowRingForm(false);
    });
  }

  return (
    <div className="space-y-4">
      {currentBoutsAll.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-bold uppercase tracking-wider text-green-600 px-1 inline-flex items-center gap-1.5">
            <DotIcon className="w-5 h-5 animate-pulse" /> {t.common.live} ({boutCount(currentBoutsAll.length)})
          </div>
          <LiveBoutRows bouts={currentBoutsAll} variant="overview" />
        </div>
      )}

      {(() => {
        const stats = [
          { label: t.tournament.bouts, value: bouts.length, tab: 'bouts' },
          { label: t.tournament.brackets, value: brackets.length, tab: 'brackets' },
          { label: t.tournament.athletes, value: registrations.length, tab: 'registrations' },
          { label: t.tournament.officials, value: referees.length, tab: 'officials' },
        ].filter(s => s.value > 0);
        return stats.length > 0 ? (
          <div className="flex gap-1.5">
            {stats.map(s => (
              <button key={s.tab} onClick={() => setTab(s.tab)} className="card px-2 py-1.5 text-center hover:shadow-md transition-shadow cursor-pointer flex-1 min-w-0">
                <span className="text-xl font-black block">{s.value}</span>
                <span className="text-[11px] text-gray-500 leading-tight">{s.label}</span>
              </button>
            ))}
          </div>
        ) : null;
      })()}

      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">{t.common.rings} ({rings.length})</div>
          {managesThisTournament && bouts.length === 0 && <button onClick={() => setShowRingForm(s => !s)} className="btn-outline btn-sm">
            {showRingForm ? t.common.cancel : t.tournament.addRing}
          </button>}
        </div>
        {showRingForm && (
          <form onSubmit={handleAddRing} className="space-y-3 mb-3">
            <RingFields draft={newRing} onChange={setNewRing} ageCodes={ringAgeCodes} />
            <button type="submit" disabled={addingRing.busy} className="btn-primary btn-sm">{t.common.add}</button>
          </form>
        )}
        {rings.length === 0 && <p className="text-xs text-gray-400">{t.tournament.noRings}{managesThisTournament ? t.tournament.addRingsHint : ''}</p>}
        <div className="divide-y divide-gray-100">
          {rings.map(r => {
            const liveHere = currentBoutsByRing[r.id];
            return (
              <div key={r.id} className="py-2 first:pt-0 last:pb-0">
                <div className="flex items-center gap-2">
                  {liveHere && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />}
                  <span className="text-sm font-semibold">{r.name}</span>
                  {(r.min_age_group || r.max_age_group) && (
                    <span className={`badge badge-amber ${BADGE_XXS_TALL} rounded-full whitespace-nowrap`}>
                      {r.min_age_group && r.max_age_group ? `${r.min_age_group} – ${r.max_age_group}` :
                       r.min_age_group ? `${r.min_age_group}+` : `≤ ${r.max_age_group}`}
                    </span>
                  )}
                  {r.max_weight_kg != null && (
                    <span className={`badge badge-blue ${BADGE_XXS_TALL} rounded-full whitespace-nowrap`}>
                      ≤ {Number(r.max_weight_kg)} kg
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-3 shrink-0">
                    <Link to={`/ring-display/${id}/${r.id}`} target="_blank"
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">{t.tournament.ringDisplayLink}</Link>
                    {/* #604 — the broadcast overlay of the ring, for a live stream. */}
                    <Link to={`/ring-overlay/${id}/${r.id}`} target="_blank" data-testid={`ring-overlay-link-${r.id}`}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">{t.tournament.ringOverlayLink}</Link>
                    {managesThisTournament && !liveHere && bouts.length === 0 && (
                      <button onClick={() => setEditingRing({ id: r.id, ...ringDraftOf(r) })}
                        title={t.tournament.editRingTitle} aria-label={t.tournament.editRingTitle}
                        className="btn-icon"><PencilIcon className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tournament Managers */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">{t.tournament.managersTitle} ({managers.length})</div>
          {managesThisTournament && !allScored && <button onClick={async () => {
            if (!showAddManager) {
              try {
                const res = await getUsers({ role: 'tournament_manager' });
                const existing = new Set(managers.map(m => m.id));
                setTmUsers(res.data.filter(u => !existing.has(u.id)));
              } catch (err) {
                // #457 — a failed load showed an empty manager picker.
                setTmUsers([]);
                showError(err);
                return;
              }
            }
            setShowAddManager(s => !s);
          }} data-testid="add-manager-btn" className="btn-outline btn-sm">
            {showAddManager ? 'Cancel' : '+ Add manager'}
          </button>}
        </div>
        {showAddManager && (
          <div className="flex gap-2 mb-3">
            <select className="input flex-1" value={managerEmail} onChange={e => setManagerEmail(e.target.value)}>
              <option value="">— Select Tournament Manager —</option>
              {tmUsers.map(u => (
                <option key={u.id} value={u.id}>{personName(u.first_name, u.last_name)} ({u.email})</option>
              ))}
            </select>
            <button
              disabled={!managerEmail || addingManager.busy}
              onClick={() => addingManager.run(async () => {
                try {
                  await addTournamentManager(id, managerEmail);
                } catch (err) { showError(err); return; }
                setManagerEmail('');
                setShowAddManager(false);
                // #419 — the manager is added. A failed refetch is a warning, not an error.
                await afterWrite({ source: 'OverviewTab:add-manager', refresh: reload });
              })}
              className="btn-primary btn-sm">{t.common.add}</button>
          </div>
        )}
        <div className="space-y-2">
          {managers.map(m => (
            <div key={m.id} className="flex items-center gap-2 text-sm">
              <Avatar photoUrl={m.photo_url} firstName={m.first_name} lastName={m.last_name} size="sm" />
              <div className="min-w-0">
                <span className="font-medium">{personName(m.first_name, m.last_name)}</span>
                {m.phone && (
                  <div className="text-xs text-gray-400">
                    <a href={`tel:${m.phone}`} className="text-blue-500 hover:underline inline-flex items-center gap-1"><PhoneIcon className="w-3 h-3" /> {m.phone}</a>
                  </div>
                )}
              </div>
              {managesThisTournament && managers.length > 1 && (
                <button onClick={async () => {
                  // #665 — ask before run(): the question must not hold the lock.
                  if (!(await confirm({ message: t.tournament.removeManagerConfirm.replace('{name}', personName(m.first_name, m.last_name)), confirmLabel: t.common.remove, destructive: true }))) return;
                  await removingManager.run(m.id, async () => {
                    try {
                      await removeTournamentManager(id, m.id);
                    } catch (err) { showError(err); return; }
                    // #419 — the manager is removed. A failed refetch is a warning, not an error.
                    await afterWrite({ source: 'OverviewTab:remove-manager', refresh: reload });
                  });
                }} disabled={removingManager.isBusy(m.id)} className="ml-auto p-2 -m-2 text-red-400 hover:text-red-600 disabled:opacity-40"
                  title={t.tournament.removeManager} aria-label={t.tournament.removeManager}><XMarkIcon className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Edit Ring modal */}
      {editingRing && (
        <ModalBackdrop onClose={() => setEditingRing(null)} maxWidth="sm" ariaLabel={t.tournament.editRingTitle}>
          <ModalHeader
            title={t.tournament.editRingTitle}
            subtitle={editingRing.name}
          />
          <RingFields draft={editingRing} onChange={draft => setEditingRing({ ...editingRing, ...draft })} ageCodes={ringAgeCodes} />
          <ModalFooter
            actions={[{
              label: t.common.delete, destructive: true, disabled: ringEdit.busy, testId: 'ring-delete',
              onClick: async () => {
                // #665 — ask before run(): the question must not hold the lock.
                if (!(await confirm({ message: t.tournament.deleteRingConfirm.replace('{name}', editingRing.name), confirmLabel: t.common.delete, destructive: true }))) return;
                await ringEdit.run(async () => {
                  try {
                    await deleteRing(id, editingRing.id);
                  } catch (err) { showError(err); return; }
                  // #419 — the ring is deleted. A failed refetch is a warning, not an error.
                  await afterWrite({ source: 'OverviewTab:delete-ring', success: t.tournament.ringDeleted.replace('{name}', editingRing.name), refresh: refreshTournament });
                  setEditingRing(null);
                });
              },
            }]}
            onSave={() => ringEdit.run(async () => {
              try {
                await api.patch(`/api/tournaments/${id}/rings/${editingRing.id}`, ringBody(editingRing));
              } catch (err) { showError(err); return; }
              // #419 — the change committed. A failed refetch is a warning, not an error.
              await afterWrite({ source: 'OverviewTab:edit-ring', success: t.tournament.ringUpdated, refresh: refreshTournament });
              setEditingRing(null);
            })}
            // An empty name keeps the old name on the server and shows "Ring updated": the button is off.
            saveDisabled={!editingRing.name.trim()}
            busy={ringEdit.busy}
            onCancel={() => setEditingRing(null)}
          />
        </ModalBackdrop>
      )}
    </div>
  );
}
